import { WorkingDaysForm } from '@/views/emails/components/threadEmails/components/newProcessDialog/wizard/util/workingDays.util.ts';

export type ProcessWizardTranslation = {
  ai: {
    badge: string;
    error: string;
    loading: string;
    loadingFromNote: string;
    lowConfidence: string;
    lowConfidenceFromNote: string;
    regenerate: string;
    source: string;
  };
  button: {
    back: string;
    cancel: string;
    create: string;
    createAndOffer: string;
    next: string;
  };
  confirm: {
    checkbox: string;
    email: string;
    hint: string;
    hintFromNote: string;
    question: string;
    readTitle: string;
  };
  created: {
    content: string;
    goToProcess: string;
    stay: string;
    title: string;
  };
  customer: {
    contactNoEmail: string;
    contactSection: string;
    customerSection: string;
    existingCustomer: {
      action: string;
      addLabel: string;
      labelTaken: string;
      text: string;
    };
    fields: {
      companyPosition: string;
      distinguishingLabel: string;
      email: string;
      firstName: string;
      houseNo: string;
      lastName: string;
      locality: string;
      name: string;
      nip: string;
      phone: string;
      postCode: string;
      regon: string;
      street: string;
    };
    gus: {
      button: string;
      filled: string;
    };
    matchesTitle: string;
    matchOwner: string;
    noMatches: string;
    noMatchesFromNote: string;
    nothingSelected: string;
    ownOrganisationEmail: string;
    pick: {
      contact: string;
      customer: string;
    };
    senderMismatch: string;
    source: {
      existingCustomerNewContact: string;
      label: string;
      manual: string;
      match: string;
      newCustomer: string;
    };
    typeLabel: string;
    types: {
      business: string;
      foreign: string;
      individual: string;
    };
  };
  description: {
    detected: string;
    detectedFromNote: string;
    label: string;
    placeholder: string;
  };
  name: {
    label: string;
    otherOwnerHint: string;
    owner: string;
    suggestions: string;
  };
  nextAction: {
    none: { description: string; title: string };
    offer: { description: string; prefilled: string; title: string };
    paramDate: string;
    paramGuests: string;
    paramLocation: string;
    question: string;
    reply: { description: string; noEmail: string; notOwner: string; title: string };
  };
  owner: {
    contactOwner: { description: string };
    hint: string;
    me: { description: string };
    question: string;
    recommended: string;
    unknownOwner: string;
  };
  reminder: {
    dueDate: string;
    explanation: string;
    goToOffer: string;
    goToOfferHint: string;
    promised: string;
    promisedTask: string;
    shift: string;
    workingDays: Record<WorkingDaysForm, string>;
  };
  reply: {
    addCafeRoyalInfo: string;
    attachments: string;
    body: string;
    contentType: string;
    draftHint: string;
    draftHintFromNote: string;
    edit: string;
    finishEditing: string;
    generate: string;
    generateHint: string;
    instruction: string;
    instructionPlaceholder: string;
    missingDetailsHint: string;
    offerDue: string;
    offerDueHint: string;
    regenerate: string;
    saveAsDraft: string;
  };
  send: {
    keepDraft: string;
    noReply: string;
    question: string;
    recipient: string;
    sendNow: string;
    sendWarning: string;
  };
  sourcePreview: {
    from: string;
    subject: string;
    title: string;
    titleNote: string;
    unavailable: string;
  };
  steps: {
    confirm: string;
    customer: string;
    description: string;
    name: string;
    nextAction: string;
    owner: string;
    reminder: string;
    reply: string;
    send: string;
  };
  subtitle: string;
  summary: {
    contact: string;
    customer: string;
    name: string;
    no: string;
    offer: string;
    offerYes: string;
    owner: string;
    reminder: string;
    reminderNow: string;
    reminderPromised: string;
    reply: string;
    replyDraft: string;
    replySend: string;
    title: string;
  };
  title: string;
  titleFromNote: string;
  validation: {
    contactEmailIsOurs: string;
    contactNameRequired: string;
    customerAlreadyExists: string;
    customerNameRequired: string;
    customerNipRequired: string;
    customerRequired: string;
    nameTooShort: string;
    partyNotConfirmed: string;
    replyNotGenerated: string;
    submitFailed: string;
    submitFailedDetail: string;
  };
};

export const processWizardTranslation: ProcessWizardTranslation = {
  ai: {
    badge: 'Propozycja AI',
    error: 'Nie udało się przygotować podpowiedzi. Uzupełnij dane ręcznie.',
    loading: 'Analizuję wątek…',
    loadingFromNote: 'Analizuję notatkę…',
    lowConfidence: 'W mailu było mało danych — sprawdź uważnie uzupełnione pola.',
    lowConfidenceFromNote: 'W notatce było mało danych — sprawdź uważnie uzupełnione pola.',
    regenerate: 'Generuj ponownie',
    source: 'Na podstawie treści wątku',
  },
  button: {
    back: 'Wstecz',
    cancel: 'Anuluj',
    create: 'Utwórz proces',
    createAndOffer: 'Utwórz proces i przejdź do oferty',
    next: 'Dalej',
  },
  confirm: {
    checkbox: 'Potwierdzam — klient i kontakt zgadzają się z zapytaniem',
    email: 'Adres e-mail',
    hint: 'Porównaj dane z treścią zapytania. Jeśli coś się nie zgadza, wróć do poprzedniego kroku i popraw.',
    hintFromNote:
      'Porównaj dane z treścią notatki. Jeśli coś się nie zgadza, wróć do poprzedniego kroku i popraw.',
    question: 'Czy dane klienta zgadzają się z zapytaniem?',
    readTitle: 'Co zostanie zapisane',
  },
  created: {
    content: 'Proces „{{name}}” został założony. Otworzyć go teraz?',
    goToProcess: 'Przejdź do procesu',
    stay: 'Zostań w skrzynce',
    title: 'Proces założony',
  },
  customer: {
    contactNoEmail: 'brak adresu e-mail',
    contactSection: 'Kontakt',
    customerSection: 'Klient',
    existingCustomer: {
      action: 'Dopnij nowy kontakt',
      addLabel: 'Jeśli to jednak inna osoba, wpisz wyróżnik — np. miasto albo firmę.',
      labelTaken: 'Ten wyróżnik jest już zajęty — podaj inny.',
      text: 'Klient „{{name}}” jest już w bazie — drugiego o tej samej nazwie nie założysz.',
    },
    fields: {
      companyPosition: 'Stanowisko',
      distinguishingLabel: 'Wyróżnik',
      email: 'Email',
      firstName: 'Imię',
      houseNo: 'Nr',
      lastName: 'Nazwisko',
      locality: 'Miejscowość',
      name: 'Nazwa / imię i nazwisko',
      nip: 'NIP',
      phone: 'Telefon',
      postCode: 'Kod pocztowy',
      regon: 'REGON',
      street: 'Ulica',
    },
    gus: {
      button: 'Pobierz z GUS',
      filled: 'Dane uzupełnione z GUS na podstawie NIP-u z maila.',
    },
    matchesTitle: 'Znalezione w bazie',
    matchOwner: 'Opiekun: {{name}}',
    noMatches: 'Nadawcy nie ma w bazie — poniżej dane odczytane z maila.',
    noMatchesFromNote: 'Tego kontaktu nie ma w bazie — poniżej dane odczytane z notatki.',
    nothingSelected: 'Nie wybrano',
    ownOrganisationEmail:
      'To adres z naszej domeny — nie zakładamy na nim klienta ani kontaktu. Wskaż klienta i kontakt z bazy albo popraw adres na adres klienta.',
    pick: {
      contact: 'Wybierz kontakt',
      customer: 'Wybierz klienta',
    },
    senderMismatch:
      'Podpis pod mailem nie zgadza się z adresem nadawcy — sprawdź, kto ma być kontaktem.',
    source: {
      existingCustomerNewContact: 'Istniejący klient + nowy kontakt',
      label: 'Skąd wziąć klienta i kontakt',
      manual: 'Wybiorę z bazy',
      match: 'Dopasowanie z bazy',
      newCustomer: 'Nowy klient + nowy kontakt',
    },
    typeLabel: 'Typ klienta',
    types: {
      business: 'Firma',
      foreign: 'Firma zagraniczna',
      individual: 'Osoba prywatna',
    },
  },
  description: {
    detected: 'Wychwycone w mailu',
    detectedFromNote: 'Wychwycone w notatce',
    label: 'Opis procesu',
    placeholder: 'Opisz, czego dotyczy zapytanie…',
  },
  name: {
    label: 'Nazwa procesu',
    otherOwnerHint:
      'Proces poprowadzi inna osoba — kreator nie przygotuje odpowiedzi do klienta w Twoim imieniu.',
    owner: 'Właściciel procesu',
    suggestions: 'Propozycje nazwy',
  },
  nextAction: {
    none: {
      description: 'Sam proces. Odpowiedź i ofertę zrobisz później z jego poziomu.',
      title: 'Nic więcej — tylko załóż proces',
    },
    offer: {
      description:
        'Proces powstaje od razu, a Ty trafiasz prosto w okno „Szybka oferta” i układanie menu.',
      prefilled: 'Z zapytania mamy już: {{params}}.',
      title: 'Przejdź do szybkiej oferty',
    },
    paramDate: 'termin',
    paramGuests: 'liczbę gości',
    paramLocation: 'miejsce',
    question: 'Co robimy zaraz po założeniu procesu?',
    reply: {
      description: 'Przygotuj odpowiedź do klienta, zdecyduj o wysyłce i o terminie przypomnienia.',
      noEmail:
        'Niedostępne — kontakt nie ma adresu e-mail, więc nie ma z czego zrobić wiadomości. Adres uzupełnisz w kroku „Klient i kontakt”.',
      notOwner: 'Niedostępne — proces prowadzi inna osoba, a mail podpisuje konkretny człowiek.',
      title: 'Odpisz klientowi',
    },
  },
  owner: {
    contactOwner: {
      description:
        'Ta osoba prowadzi wskazany kontakt. Proces trafi do niej, a Ty nie będziesz mógł odpisać klientowi z tego kreatora.',
    },
    hint: 'Wskazany kontakt prowadzi {{name}}, a proces zakładasz Ty. Wybierz, kto ma go poprowadzić.',
    me: {
      description:
        'Proces zostaje u Ciebie. Opiekun kontaktu się nie zmienia — dotyczy to tylko tego procesu.',
    },
    question: 'Kto ma prowadzić ten proces?',
    recommended: 'Sugerowane',
    unknownOwner: 'Opiekun kontaktu',
  },
  reminder: {
    dueDate: 'Termin',
    explanation:
      'Zakładając proces, system tworzy zadanie „Skontaktuj się z klientem” z terminem na teraz. Skoro klient dostał obietnicę odpowiedzi, warto przesunąć je na ten sam termin.',
    goToOffer: 'Po utworzeniu przejdź do szybkiej oferty',
    goToOfferHint:
      'Otworzy się okno „Szybka oferta” z terminem, liczbą gości i miejscem odczytanymi z zapytania.',
    promised:
      'To termin, w którym zobowiązujesz się przesłać klientowi ofertę. Zostanie zapisany w zadaniu razem z opisem ustaleń.',
    promisedTask: 'Zadanie „Skontaktuj się z klientem”',
    shift: 'Przesuń zadanie „Skontaktuj się z klientem”',
    workingDays: {
      few: '{{count}} dni robocze',
      many: '{{count}} dni roboczych',
      one: '{{count}} dzień roboczy',
    },
  },
  reply: {
    addCafeRoyalInfo: 'Dodaj informację o Royal Restaurant',
    attachments: 'Załączniki',
    body: 'Treść odpowiedzi',
    contentType: 'Rodzaj odpowiedzi',
    draftHint:
      'Nic nie zostanie wysłane. Szkic trafi do wątku i do nowego procesu, gotowy do pracy.',
    draftHintFromNote:
      'Nic nie zostanie wysłane. Powstanie nowa wiadomość do kontaktu, wpięta w nowy proces, gotowa do pracy.',
    edit: 'Popraw treść',
    finishEditing: 'Gotowe',
    generate: 'Przygotuj treść',
    generateHint: 'Kliknij „Przygotuj treść”, żeby zobaczyć wiadomość przed akceptacją.',
    instruction: 'Instrukcja dla AI',
    instructionPlaceholder: 'Napisz, o czym ma być wiadomość…',
    missingDetailsHint:
      'W zapytaniu brakuje terminu lub liczby gości — rozważ szablon „Opcjonalnie - weryfikacja potrzeb”.',
    offerDue: 'Kiedy wrócisz z ofertą',
    offerDueHint:
      'AI wpisze ten termin do maila, a zadanie „Skontaktuj się z klientem” dostanie go jako przypomnienie.',
    regenerate: 'Generuj ponownie',
    saveAsDraft: 'Przygotuj szkic odpowiedzi',
  },
  send: {
    keepDraft: 'Zostaw w szkicach — wyślę sam po przejrzeniu',
    noReply: 'Odpowiedź nie będzie przygotowywana, więc nie ma czego wysyłać.',
    question: 'Wysłać tę wiadomość do klienta?',
    recipient: 'Odbiorca',
    sendNow: 'Wyślij teraz',
    sendWarning: 'Wiadomość wyjdzie do klienta zaraz po utworzeniu procesu.',
  },
  sourcePreview: {
    from: 'Od',
    subject: 'Temat',
    title: 'Zapytanie, z którego uzupełniliśmy dane',
    titleNote: 'Notatka, z której uzupełniliśmy dane',
    unavailable: 'Nie udało się wczytać treści wiadomości.',
  },
  steps: {
    confirm: 'Potwierdzenie',
    customer: 'Klient i kontakt',
    description: 'Opis',
    name: 'Nazwa',
    nextAction: 'Dalszy krok',
    owner: 'Właściciel',
    reminder: 'Przypomnienie',
    reply: 'Odpowiedź',
    send: 'Wysyłka',
  },
  subtitle: 'Dane wstępnie uzupełnione przez AI — sprawdź i zatwierdź',
  summary: {
    contact: 'Kontakt',
    customer: 'Klient',
    name: 'Nazwa',
    no: 'Nie',
    offer: 'Oferta',
    offerYes: 'Przejście do szybkiej oferty',
    owner: 'Właściciel',
    reminder: 'Przypomnienie',
    reminderNow: 'Na teraz (bez zmian)',
    reminderPromised: '{{date}} — termin obiecanej oferty',
    reply: 'Odpowiedź',
    replyDraft: 'Szkic w wątku',
    replySend: 'Wysyłka od razu',
    title: 'Co powstanie',
  },
  title: 'Nowy proces z maila',
  titleFromNote: 'Nowy proces z notatki',
  validation: {
    contactEmailIsOurs: 'Adres z naszej domeny nie może być kontaktem — wskaż klienta z bazy',
    contactNameRequired: 'Uzupełnij imię i nazwisko kontaktu',
    customerAlreadyExists:
      'Ten klient jest już w bazie — wybierz jedno z rozwiązań z ostrzeżenia powyżej',
    customerNameRequired: 'Uzupełnij nazwę klienta',
    customerNipRequired: 'Uzupełnij NIP klienta — pobierz dane z GUS albo wpisz go ręcznie',
    customerRequired: 'Wybierz klienta i kontakt',
    nameTooShort: 'Nazwa musi mieć co najmniej 3 znaki',
    partyNotConfirmed: 'Potwierdź, że klient i kontakt zgadzają się z zapytaniem',
    replyNotGenerated: 'Przygotuj treść odpowiedzi albo odznacz przygotowanie szkicu',
    submitFailed: 'Nie udało się założyć procesu. Spróbuj ponownie.',
    submitFailedDetail: 'Nie udało się założyć procesu: {{detail}}',
  },
};
